import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Card,
  Modal,
  Portal,
} from '@chargify/custom-ui-components';
import { formatDate } from '@chargify/formatters';
import { useToastMessages } from '@chargify/toasts';
import { subscriptionsSelectors } from '../../../../machines';
import { GlobalStateContext } from '../../../../contexts';
import { useCancelSubscriptionService } from '../../../../hooks';
import { cancelSubscriptionSelectors } from '../../../../machines/cancelSubscription';
import { CancelOptions } from '../../constants';
import { useCancelForm } from '../../hooks/useCancelForm';
import { Form } from '../Form';

type CancelSubscriptionProps = {
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: () => void;
  /**
   * Catalog id
   */
  catalogId: number;
  /**
   * Subscription id
   */
  subscriptionId: number;
  /**
   * Available cancel options
   */
  availableCancelOptions: CancelOptions[];
  /**
   * Churn reason codes visibility
   */
  enabledChurnReasonCodes: boolean;
};

const Content = ({
  onClose,
  displayMode,
  catalogId,
  subscriptionId,
  availableCancelOptions,
  enabledChurnReasonCodes,
}: CancelSubscriptionProps) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const modalRef = useRef(null);
  const portalContainer = useRef(null);

  const { subscriptionsService } = useContext(GlobalStateContext);
  const { subscription } = useSelector(
    subscriptionsService,
    useCallback(
      subscriptionsSelectors.getSubscriptionById(catalogId, subscriptionId),
      []
    )
  );
  const cancelService = useCancelSubscriptionService(catalogId, subscriptionId);

  const {
    cancelSubscriptionSuccess,
    cancelSubscriptionError,
    cancelSubscriptionLoading,
  } = useSelector(
    cancelService,
    cancelSubscriptionSelectors.getCancelSubscriptionStatus
  );

  const {
    context,
    send,
    isValid: isFormValid,
    isLoading: isFormLoading,
    hasError: hasFormError,
  } = useCancelForm(enabledChurnReasonCodes, availableCancelOptions);

  const {
    cancellationType,
    cancellationDate,
    cancellationMessage,
    cancellationChurnReasonCode,
  } = context;

  const toastService = useToastMessages();
  const cancelDateTime =
    cancellationType === CancelOptions.SCHEDULED_CANCEL
      ? cancellationDate
      : subscription.nextAssessmentAt;

  useEffect(() => {
    if (cancelSubscriptionSuccess) {
      onClose();

      const toastMessage =
        cancellationType === CancelOptions.IMMEDIATE_CANCEL
          ? t('cancel_subscription.cancel_success')
          : t('cancel_subscription.cancel_scheduled_success', {
              date: formatDate(cancelDateTime),
            });

      toastService.show({
        level: 'success',
        message: toastMessage,
      });
    }
  }, [cancelSubscriptionSuccess]);

  const alertMsg = () => {
    if (cancellationType === CancelOptions.IMMEDIATE_CANCEL) {
      return t('cancel_subscription.you_will_lose_access_immediately');
    }
    return t('cancel_subscription.you_will_lose_access_on_date', {
      date: formatDate(cancelDateTime),
    });
  };

  const onCancelSubscription = () => {
    if (cancellationType === CancelOptions.IMMEDIATE_CANCEL) {
      return cancelService.send({
        type: 'CANCEL_SUBSCRIPTION',
        cancellationMessage,
        churnReasonCode: cancellationChurnReasonCode,
      });
    }
    if (cancellationType === CancelOptions.DELAYED_CANCEL) {
      return cancelService.send({
        type: 'CANCEL_SUBSCRIPTION',
        cancelAtEndOfPeriod: true,
        cancellationMessage,
        churnReasonCode: cancellationChurnReasonCode,
      });
    }
    return cancelService.send({
      type: 'CANCEL_SUBSCRIPTION',
      scheduledCancellationAt: cancelDateTime,
      cancellationMessage,
      churnReasonCode: cancellationChurnReasonCode,
    });
  };

  return (
    <>
      <Card
        border={displayMode === 'layer' ? 'sharp' : 'rounded'}
        ref={modalRef}
      >
        <Card.Header>
          <Modal.Title
            onClose={onClose}
            title={t('cancel_subscription.cancel_title')}
          />
        </Card.Header>
        <Card.Body>
          {cancelSubscriptionError || hasFormError ? (
            <Alert
              color="danger"
              title={t('cancel_subscription.cancel_error_title')}
            >
              <Alert.TextMessage>
                {t('cancel_subscription.cancel_error_description')}
              </Alert.TextMessage>
            </Alert>
          ) : (
            <>
              <Form
                isLoading={isFormLoading}
                modalRef={modalRef}
                portalContainer={portalContainer}
                subscriptionNextAssessmentDate={subscription.nextAssessmentAt}
                availableCancelOptions={availableCancelOptions}
                values={context}
                onChange={(name, value) =>
                  send({
                    type: 'SET_FORM_VALUE',
                    name: name,
                    value: value,
                  })
                }
              />
              <Alert
                color="danger"
                title={t(
                  'cancel_subscription.are_you_sure_you_want_to_cancel',
                  {
                    planName: subscription.product.name,
                  }
                )}
              >
                <Alert.TextMessage>{alertMsg()}</Alert.TextMessage>
              </Alert>
            </>
          )}
        </Card.Body>
        <Card.Footer>
          <Button.Group alignment="right">
            {cancelSubscriptionError ? (
              <Button
                color="secondary"
                label={t('close', { ns: 'common' })}
                onClick={onClose}
              />
            ) : (
              <>
                <Button
                  color="secondary"
                  label={t('cancel_subscription.keep_active')}
                  onClick={onClose}
                  disabled={cancelSubscriptionLoading}
                />
                <Button
                  color="danger"
                  label={t('cancel_subscription.cancel')}
                  isLoading={cancelSubscriptionLoading}
                  disabled={!isFormValid}
                  onClick={() => onCancelSubscription()}
                />
              </>
            )}
          </Button.Group>
        </Card.Footer>
      </Card>
      <Portal>
        <div ref={portalContainer}></div>
      </Portal>
    </>
  );
};

export default Content;
